import { Button, Dialog, notify, StatusPill, TextAreaField, TextField } from "@flexfreight/ui";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError } from "../../api/client";

type Draft = { to: string | null; subject: string; body: string; sent: false; testRates: boolean };
const copy = (text: string, what: string) =>
  navigator.clipboard?.writeText(text).then(
    () => notify.success(`${what} copied`),
    () => notify.error("Couldn't copy", "Select the text and copy it instead."),
  );

/** A.8.1: customer-facing quote PDF (sell prices only) and a copy-ready email. Nothing is sent from the console. */
export function QuoteShare({ quoteId }: { quoteId: string }) {
  const draft = useMutation({ mutationFn: () => api<Draft>(`/v1/console/quotes/${quoteId}/email-draft`), onError: (e) => notify.error("Couldn't draft the email", e instanceof ApiError ? e.message : "Try again.") });
  return (
    <div className="ffa-integration__actions">
      <a className="ffx-btn ffx-btn--sm" href={`/v1/console/quotes/${quoteId}/document`} download>Download PDF</a>
      <Button size="sm" onClick={() => draft.mutate()} loading={draft.isPending}>Email draft</Button>
      {draft.data ? (
        <Dialog
          open
          onClose={() => draft.reset()}
          title="Quote email"
          description="Copy this into your email with the PDF attached. The console doesn't send email yet."
          footer={<Button variant="primary" onClick={() => draft.reset()}>Done</Button>}
        >
          <div className="ffa-form">
            {draft.data.testRates ? (
              <div className="ffa-banner" role="alert">
                <StatusPill tone="warning">Test rates</StatusPill>
                <span>These prices come from mock or sandbox carriers. Don't send them to a customer.</span>
              </div>
            ) : null}
            <TextField label="To" value={draft.data.to ?? ""} readOnly hint={draft.data.to ? undefined : "No customer contact on file: add the recipient yourself."} />
            <TextField label="Subject" value={draft.data.subject} readOnly />
            <TextAreaField label="Message" value={draft.data.body} readOnly rows={12} />
            <div className="ffa-integration__actions">
              <Button size="sm" onClick={() => void copy(draft.data!.subject, "Subject")}>Copy subject</Button>
              <Button size="sm" onClick={() => void copy(draft.data!.body, "Message")}>Copy message</Button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
